import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/ascend/AppShell";
import AffirmationCard from "@/components/ascend/AffirmationCard";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Lock, ArrowRight, Sparkles, Heart } from "lucide-react";

const MOODS = [
  { v: 1, emoji: "😔", label: "heavy" },
  { v: 2, emoji: "😐", label: "meh" },
  { v: 3, emoji: "🙂", label: "okay" },
  { v: 4, emoji: "😊", label: "good" },
  { v: 5, emoji: "🌟", label: "shining" },
];

const WIN_PLACEHOLDERS = [
  "a moment that made you smile",
  "something small you're proud of",
  "one thing that went right",
  "a chapter you understood better",
  "a page you finished when you didn't want to",
];

const GRATITUDE_PLACEHOLDERS = [
  "who or what are you grateful for today?",
  "a small kindness you noticed",
  "something you don't want to forget",
  "a person who made today easier",
  "a place, a song, or a warm cup",
];

export default function Checkin() {
  const navigate = useNavigate();
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mood, setMood] = useState(null);
  const [win, setWin] = useState("");
  const [gratitude, setGratitude] = useState("");
  const [priv, setPriv] = useState("");
  const [busy, setBusy] = useState(false);
  const [shownAffirmation, setShownAffirmation] = useState(null);
  const [placeholder] = useState(() => WIN_PLACEHOLDERS[Math.floor(Math.random() * WIN_PLACEHOLDERS.length)]);
  const [gratitudePlaceholder] = useState(() => GRATITUDE_PLACEHOLDERS[Math.floor(Math.random() * GRATITUDE_PLACEHOLDERS.length)]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/checkins/today");
        if (data.checkin) {
          setExisting(data.checkin);
          setMood(data.checkin.mood);
          setWin(data.checkin.win_text || "");
          setGratitude(data.checkin.gratitude_text || "");
          setPriv(data.checkin.private_note || "");
          setShownAffirmation({ text: data.checkin.affirmation_shown, source: data.checkin.affirmation_source });
        }
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const canSubmit = mood != null;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      let data;
      if (existing) {
        const { data: r } = await api.patch(`/checkins/${existing.date}`, {
          mood, win_text: win, gratitude_text: gratitude, private_note: priv,
        });
        data = r;
        toast.success("Updated today.");
      } else {
        const { data: r } = await api.post("/checkins", {
          mood, win_text: win, gratitude_text: gratitude, private_note: priv,
        });
        data = r;
      }
      setExisting(data);
      setShownAffirmation({ text: data.affirmation_shown, source: data.affirmation_source });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <AppShell><div className="text-muted">Loading…</div></AppShell>;

  return (
    <AppShell>
      <Toaster richColors position="top-center" />
      <div className="pt-2 max-w-2xl mx-auto">
        <div className="mb-6 fade-up">
          <span className="eyebrow">just a minute with yourself</span>
          <h1 className="font-display text-4xl sm:text-5xl mt-1" style={{ color: "var(--text)" }}>
            {existing ? "Today, revisited" : "Today's check-in"}
          </h1>
          <p className="text-muted mt-1">
            {existing ? "You can edit today until midnight." : "Three tiny things. Under a minute."}
          </p>
        </div>

        {shownAffirmation && (
          <div className="mb-8">
            <AffirmationCard text={shownAffirmation.text} source={shownAffirmation.source} size="md" />
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => navigate("/dashboard")}
                className="btn-primary flex items-center gap-2 text-sm"
                data-testid="checkin-return-dashboard"
              >
                Back to dashboard <ArrowRight size={14} strokeWidth={1.75} />
              </button>
            </div>
          </div>
        )}

        {/* Mood */}
        <section className="card-soft p-6 sm:p-7 mb-5 fade-up" data-testid="checkin-mood-section">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={14} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
            <span className="eyebrow">how are you, really?</span>
          </div>
          <div className="grid grid-cols-5 gap-2 sm:gap-3">
            {MOODS.map((m) => (
              <button
                key={m.v}
                onClick={() => setMood(m.v)}
                data-testid={`mood-${m.v}`}
                className="flex flex-col items-center justify-center rounded-2xl py-3 transition-transform"
                style={{
                  background: mood === m.v ? "color-mix(in oklab, var(--primary) 20%, var(--surface))" : "var(--surface-2)",
                  border: `1px solid ${mood === m.v ? "var(--primary)" : "var(--border)"}`,
                  transform: mood === m.v ? "scale(1.05)" : "scale(1)",
                }}
              >
                <span className="text-3xl sm:text-4xl">{m.emoji}</span>
                <span className="text-[10px] uppercase tracking-wider mt-1 text-muted">{m.label}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Win */}
        <section className="card-soft p-6 sm:p-7 mb-5 fade-up delay-1" data-testid="checkin-win-section">
          <div className="mb-3">
            <span className="eyebrow">a small win</span>
            <div className="font-display text-2xl leading-tight mt-1" style={{ color: "var(--text)" }}>
              what went well today?
            </div>
          </div>
          <textarea
            className="input-soft"
            placeholder={placeholder}
            value={win}
            onChange={(e) => setWin(e.target.value)}
            maxLength={1200}
            data-testid="checkin-win-input"
            rows={3}
            style={{ minHeight: 96 }}
          />
        </section>

        {/* Gratitude — private-styled, never shared */}
        <section
          className="rounded-3xl p-6 sm:p-7 mb-5 fade-up delay-2"
          style={{
            background: "color-mix(in oklab, var(--primary-soft) 26%, var(--surface-2))",
            border: "1px dashed var(--border)",
          }}
          data-testid="checkin-gratitude-section"
        >
          <div className="flex items-center gap-2 mb-2">
            <Heart size={14} strokeWidth={1.5} style={{ color: "var(--accent)" }} />
            <span className="eyebrow">grateful for</span>
          </div>
          <div className="font-display text-2xl leading-tight mb-3 italic" style={{ color: "var(--text)" }}>
            what warmed you today?
          </div>
          <textarea
            className="input-soft"
            placeholder={gratitudePlaceholder}
            value={gratitude}
            onChange={(e) => setGratitude(e.target.value)}
            maxLength={1200}
            data-testid="checkin-gratitude-input"
            rows={3}
            style={{ minHeight: 96, background: "var(--surface)", fontFamily: "var(--font-hand, inherit)" }}
          />
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-muted italic">
            <Lock size={10} strokeWidth={1.75} />
            just for you — never shared
          </div>
        </section>

        {/* Private note */}
        <section
          className="rounded-3xl p-6 sm:p-7 mb-6 fade-up delay-3"
          style={{
            background: "color-mix(in oklab, var(--primary-soft) 20%, var(--surface-2))",
            border: "1px dashed var(--border)",
          }}
          data-testid="checkin-private-section"
        >
          <div className="flex items-center gap-2 mb-2">
            <Lock size={14} strokeWidth={1.5} style={{ color: "var(--accent)" }} />
            <span className="eyebrow">just for you — never shared</span>
          </div>
          <div className="font-display text-2xl leading-tight mb-3" style={{ color: "var(--text)" }}>
            anything else on your mind?
          </div>
          <textarea
            className="input-soft"
            placeholder="It's yours. Write freely, or leave it blank."
            value={priv}
            onChange={(e) => setPriv(e.target.value)}
            maxLength={4000}
            data-testid="checkin-private-input"
            rows={5}
            style={{ minHeight: 140, background: "var(--surface)" }}
          />
        </section>

        <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
          <button onClick={() => navigate("/dashboard")} className="btn-ghost text-sm w-full sm:w-auto" data-testid="checkin-skip-btn">
            Save for later
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit || busy}
            className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto disabled:opacity-60"
            data-testid="checkin-submit-btn"
          >
            {busy ? "Saving…" : existing ? "Save changes" : "Save & see today's line"} <ArrowRight size={14} strokeWidth={1.75} />
          </button>
        </div>
      </div>
    </AppShell>
  );
}
